import React from "react";
import { Text } from "@/components/text";
import type { IQuestion } from "@/apis/exam_papers/types";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PlusIcon } from "@heroicons/react/20/solid";

const hasContent = (s?: string): boolean => {
  if (!s) return false;
  const textOnly = s
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;|&#xA0;|&#160;/gi, "")
    .trim();
  if (textOnly.length > 0) return true;
  if (/<(img|iframe|video|audio|svg|canvas)\b/i.test(s)) return true;
  if (/class\s*=\s*["'][^"']*(?:ql-formula|katex)/i.test(s)) return true;
  return false;
};

type EmptyState = "empty" | "partial" | "complete";

const getEmptyState = (q: IQuestion): EmptyState => {
  const questionFilled = hasContent(q.question);
  if (q.answer_type === "multiple_choice") {
    const choiceTexts = (q.choices ?? []).map((c) => c.text ?? "");
    const filledChoices = choiceTexts.filter(hasContent).length;
    const totalChoices = choiceTexts.length;
    const allChoicesFilled = totalChoices > 0 && filledChoices === totalChoices;
    const allChoicesEmpty = filledChoices === 0;

    if (!questionFilled && allChoicesEmpty) return "empty";
    if (questionFilled && allChoicesFilled) return "complete";
    return "partial";
  }
  return questionFilled ? "complete" : "empty";
};

interface QuestionNumberSelectorProps {
  questions: IQuestion[];
  currentQuestionIndex: number;
  onQuestionChange: (questionNumber: number) => void;
  onReorder?: (nextQuestionIds: string[]) => void;
  onAddQuestion?: () => void;
  isUpdating?: boolean;
}

interface SortableQuestionButtonProps {
  question: IQuestion;
  questionIndex: number;
  isCurrent: boolean;
  isSharedGroup: boolean;
  onClick: () => void;
  disabled: boolean;
}

const SortableQuestionButton: React.FC<SortableQuestionButtonProps> = ({
  question,
  questionIndex,
  isCurrent,
  isSharedGroup,
  onClick,
  disabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const emptyState = getEmptyState(question);
  const baseClasses =
    "w-10 h-10 rounded flex items-center justify-center select-none";
  const colorClasses = (() => {
    if (isCurrent) {
      if (emptyState === "empty") {
        return "bg-blue-600 text-white border-2 border-dashed border-blue-300";
      }
      if (emptyState === "partial") {
        return "bg-blue-600 text-white border-2 border-dashed border-amber-400";
      }
      return "bg-blue-600 text-white";
    }
    if (emptyState === "empty") {
      return isSharedGroup
        ? "bg-white text-gray-400 border-2 border-dashed border-blue-300"
        : "bg-white text-gray-400 border-2 border-dashed border-gray-300";
    }
    if (emptyState === "partial") {
      return "bg-amber-50 text-amber-700 border-2 border-dashed border-amber-400";
    }
    return isSharedGroup
      ? "bg-white hover:bg-blue-100 border border-blue-200"
      : "bg-gray-100 hover:bg-gray-200";
  })();
  const cursorClass = disabled ? "cursor-pointer" : "cursor-move";

  return (
    <button
      ref={setNodeRef}
      type="button"
      style={style}
      onClick={onClick}
      className={`${baseClasses} ${colorClasses} ${cursorClass}`}
      {...attributes}
      {...listeners}
    >
      {questionIndex + 1}
    </button>
  );
};

const QuestionNumberSelector: React.FC<QuestionNumberSelectorProps> = ({
  questions,
  currentQuestionIndex,
  onQuestionChange,
  onReorder,
  onAddQuestion,
  isUpdating = false,
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const questionIds = React.useMemo(
    () => questions.map((q) => q.id),
    [questions]
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!onReorder) return;

    const oldIndex = questionIds.indexOf(String(active.id));
    const newIndex = questionIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const nextIds = arrayMove(questionIds, oldIndex, newIndex);
    onReorder(nextIds);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const renderGroups = () => {
    const groups: {
      questions: number[];
      passageId: string | null;
    }[] = [];
    let currentGroup: {
      questions: number[];
      passageId: string | null;
    } | null = null;

    questions.forEach((question, index) => {
      const passageId = question.passage_id || null;

      if (!currentGroup || currentGroup.passageId !== passageId) {
        currentGroup = { questions: [index], passageId };
        groups.push(currentGroup);
      } else {
        currentGroup.questions.push(index);
      }
    });

    return groups.map((group, groupIndex) => {
      const isSharedGroup = !!group.passageId;

      const buttons = group.questions.map((questionIndex) => {
        const question = questions[questionIndex];
        return (
          <SortableQuestionButton
            key={question.id}
            question={question}
            questionIndex={questionIndex}
            isCurrent={currentQuestionIndex === questionIndex}
            isSharedGroup={isSharedGroup}
            onClick={() => onQuestionChange(questionIndex + 1)}
            disabled={isUpdating}
          />
        );
      });

      if (isSharedGroup) {
        return (
          <div
            key={`group-${groupIndex}`}
            className="flex flex-wrap max-w-full gap-1 p-2 bg-blue-50 border-2 border-dashed border-blue-300 rounded"
            title={`Shared Passage Group (ID: ${group.passageId})`}
          >
            {buttons}
          </div>
        );
      }

      return (
        <div key={`group-${groupIndex}`} className="flex flex-wrap max-w-full gap-1 p-2 items-center">
          {buttons}
        </div>
      );
    });
  };

  const activeQuestion = activeId
    ? questions.find((q) => q.id === activeId)
    : null;
  const activeIndex = activeQuestion
    ? questions.findIndex((q) => q.id === activeQuestion.id)
    : -1;

  return (
    <div className="mb-6 border-b pb-4">
      <Text className="mb-2 font-medium">Select Question Number:</Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={questionIds} strategy={rectSortingStrategy}>
          <div className="flex flex-wrap gap-2 items-center">
            {renderGroups()}
            {onAddQuestion && (
              <div className="flex gap-1 p-2 items-center">
                <button
                  type="button"
                  onClick={onAddQuestion}
                  disabled={isUpdating}
                  aria-label="Add question"
                  title="Add question"
                  className={`w-10 h-10 rounded flex items-center justify-center border-2 border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 ${
                    isUpdating ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                  }`}
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeQuestion ? (
            <button
              type="button"
              className="w-10 h-10 rounded flex items-center justify-center bg-blue-600 text-white shadow-lg"
            >
              {activeIndex + 1}
            </button>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default QuestionNumberSelector;
